import * as _ from 'lodash';

import { log } from './browserLogger';
import { sendEvent } from './event-tracking';

const enum FullscreenElement {
  WEBKIT_FULLSCREEN = 'webkitFullscreenElement',
  MS_FULLSCREEN = 'msFullscreenElement',
  GENERIC_FULLSCREEN = 'fullscreenElement',
}

declare global {
  interface HTMLVideoElement {
    msRequestFullscreen: () => void;
    webkitEnterFullscreen: () => void;
  }
}

declare global {
  interface Document {
    msFullscreenElement: Element;
    webkitFullscreenElement: Element;
    webkitIsFullScreen: boolean;
    msExitFullscreen(): void;
    webkitExitFullscreen(): void;
  }
}

const intervalForVideoTracking = 25;
const videoTrackingIndexForInlineIndependentVideo = 0;
const videoTrackingIndexForBreedResultsVideo = 1;
const videoTrackingIndexForFamilyTreeVideo = 2;

const videoTrackers: number[] = [intervalForVideoTracking, intervalForVideoTracking, intervalForVideoTracking];
export function init(): void {
  const videoElement = document.getElementById('breed-video');
  const breedResultsVideoElement = document.getElementById('breed-results-breed-video');
  const familyTreeVideoElement = document.getElementById('family-tree-breed-video');
  if (videoElement && !isVideoElement(videoElement)) {
    log.error('breed-video element is not a video');
    return;
  }
  if (breedResultsVideoElement && !isVideoElement(breedResultsVideoElement)) {
    log.error('breed-results-breed-video element is not a video');
    return;
  }
  if (familyTreeVideoElement && !isVideoElement(familyTreeVideoElement)) {
    log.error('family-tree-breed-video element is not a video');
    return;
  }

  const oneTimePosterImage = document.getElementById('breed-video-image');
  const breedResultsPosterImage = document.getElementById('breed-results-breed-video-image');
  const familyTreePosterImage = document.getElementById('family-tree-breed-video-image');

  if (oneTimePosterImage) {
    oneTimePosterImage.addEventListener('click', () => {
      void toggleFullScreenAndHidePosterFromImage(videoElement);
    });
  }
  if (breedResultsPosterImage) {
    breedResultsPosterImage.addEventListener('click', () => {
      void toggleFullScreenVideoFromImage(breedResultsVideoElement);
    });
  }
  if (familyTreePosterImage) {
    familyTreePosterImage.addEventListener('click', () => {
      void toggleFullScreenVideoFromImage(familyTreeVideoElement);
    });
  }

  if (videoElement) {
    const durationElement = document.getElementById('breed-video-duration');
    tryToLoadVideoAndDuration(videoElement, durationElement);
    setupListenersOnVideoElements(videoElement, videoTrackingIndexForInlineIndependentVideo);
  }
  if (breedResultsVideoElement) {
    const breedResultsDurationElement = document.getElementById('breed-results-breed-video-duration');
    tryToLoadVideoAndDuration(breedResultsVideoElement, breedResultsDurationElement);
    setupListenersForNonInlineVideo(breedResultsVideoElement, videoTrackingIndexForBreedResultsVideo);
  }
  if (familyTreeVideoElement) {
    const familyTreeDurationElement = document.getElementById('family-tree-breed-video-duration');
    tryToLoadVideoAndDuration(familyTreeVideoElement, familyTreeDurationElement);
    setupListenersForNonInlineVideo(familyTreeVideoElement, videoTrackingIndexForFamilyTreeVideo);
  }
}

async function toggleFullScreenAndHidePosterFromImage(videoElement: HTMLVideoElement | null): Promise<void> {
  const imageElement = document.getElementById('breed-reveal-video-poster-container');
  if (!videoElement) {
    log.error('requested video element does not exist');
    return;
  }

  if (imageElement) {
    imageElement.classList.add('hide');
  }

  videoElement.classList.remove('hide');
  videoElement.requestFullscreen =
    videoElement.requestFullscreen || videoElement.msRequestFullscreen || videoElement.webkitEnterFullscreen;

  if (videoElement.paused || videoElement.currentTime === 0) {
    void videoElement.play();
  } else {
    videoElement.pause();
  }

  requestFullscreen(videoElement);
}

async function toggleFullScreenVideoFromImage(videoElement: HTMLVideoElement | null): Promise<void> {
  if (!videoElement) {
    log.error('requested video element does not exist');
    return;
  }

  videoElement.classList.remove('hide');
  videoElement.currentTime = 0;
  videoElement.requestFullscreen =
    videoElement.requestFullscreen || videoElement.msRequestFullscreen || videoElement.webkitEnterFullscreen;

  if (videoElement.paused || videoElement.currentTime === 0) {
    void videoElement.play();
  }

  requestFullscreen(videoElement);
}

async function exitFullScreenFromVideo(videoElement: HTMLVideoElement): Promise<void> {
  const imageElement = document.getElementById('breed-video-image');
  if (!videoElement) {
    log.error('requested video element does not exist');
    return;
  }

  imageElement ? videoElement.classList.remove('hide') : videoElement.classList.add('hide');

  if (document.fullscreenElement && document.exitFullscreen) {
    await document.exitFullscreen();
  } else if (document.msFullscreenElement && document.msExitFullscreen) {
    document.msExitFullscreen();
  } else if (document.webkitFullscreenElement && document.webkitExitFullscreen) {
    document.webkitExitFullscreen();
  }
}

function setupListenersForNonInlineVideo(videoElement: HTMLVideoElement, videoTrackerIndex: number): void {
  setupListenersOnVideoElements(videoElement, videoTrackerIndex);
  document.addEventListener('fullscreenchange', () => {
    hideAndPauseVideo(FullscreenElement.GENERIC_FULLSCREEN, videoElement);
  });
  document.addEventListener('webkitfullscreenchange', () => {
    hideAndPauseVideo(FullscreenElement.WEBKIT_FULLSCREEN, videoElement);
  });
  document.addEventListener('MSFullscreenChange', () => {
    hideAndPauseVideo(FullscreenElement.MS_FULLSCREEN, videoElement);
  });
}

function setupListenersOnVideoElements(videoElement: HTMLVideoElement, videoTrackerIndex: number): void {
  videoElement.addEventListener('click', () => exitFullScreenFromVideo(videoElement));
  videoElement.addEventListener('timeupdate', () => trackPlayingStats(videoElement, videoTrackerIndex));
}

function hideAndPauseVideo(typeOfFullscreen: FullscreenElement, videoElement: HTMLVideoElement): void {
  if (!videoElement) {
    log.error('requested video element does not exist');
    return;
  }

  if (!document[typeOfFullscreen]) {
    videoElement.classList.add('hide');
    videoElement.pause();
  }
}

function trackPlayingStats(videoElement: HTMLVideoElement, videoTrackerIndex: number): void {
  const currentTime = videoElement.currentTime;
  const duration = videoElement.duration;
  const percentageViewed = _.clamp((currentTime / duration) * 100, 0, 100);
  const categoryAttribute = videoElement.attributes.getNamedItem('category');
  const handleAttribute = videoElement.attributes.getNamedItem('handle');

  const hasPassedCurrentMarker = percentageViewed - videoTrackers[videoTrackerIndex] >= 0;
  if (hasPassedCurrentMarker) {
    sendVideoTrackWatchingData(
      `Full Breed Reveal Video Watched ${videoTrackers[videoTrackerIndex]}%`,
      categoryAttribute,
      handleAttribute
    );
    videoTrackers[videoTrackerIndex] += intervalForVideoTracking;
  }
}

function tryToLoadVideoAndDuration(videoElement: HTMLVideoElement, durationElement: HTMLElement | null): void {
  if (!durationElement || !isParagraphElement(durationElement)) {
    log.warn('duration element either does not exist or is not a paragraph');
    return;
  }

  if (!videoElement.duration) {
    videoElement.ondurationchange = () => {
      videoLoad(videoElement, durationElement);
    };
    videoElement.load();
  } else {
    videoLoad(videoElement, durationElement);
  }
}

function videoLoad(videoElement: HTMLVideoElement, durationTextElement: HTMLParagraphElement): void {
  const duration = videoElement.duration || 0;
  const minutes = Math.floor(duration / 60);
  const seconds = Math.floor(duration % 60);
  const secondsForDisplay = `${seconds}`.padStart(2, '0');

  durationTextElement.textContent = `${minutes}:${secondsForDisplay}`;
}

function requestFullscreen(videoElement: HTMLVideoElement): void {
  if (!document.fullscreenElement) {
    try {
      void videoElement.requestFullscreen();
    } catch (e) {
      log.info(`${videoElement.id} failed to fullscreen: ${e}`);
    }
  }
}

function sendVideoTrackWatchingData(
  action: string,
  categoryAttribute: Attr | null,
  handleAttribute: Attr | null
): void {
  const category = categoryAttribute ? categoryAttribute.value : 'Breed Video Unknown Page';
  const handle = handleAttribute ? handleAttribute.value : null;
  const data = {
    eventCategory: category,
    eventAction: action,
    eventLabel: null,
    eventValue: null,
    handle: handle,
    petNum: null,
  };
  sendEvent(data);
}

function isVideoElement(element: HTMLElement): element is HTMLVideoElement {
  return (element as HTMLVideoElement).pause !== undefined && (element as HTMLVideoElement).play !== undefined;
}

function isParagraphElement(element: HTMLElement): element is HTMLParagraphElement {
  return (element as HTMLParagraphElement).textContent !== undefined;
}
